/**
 * <LiveReportDetailsTemplate> — the full live-report page (`/r?h=…`) rebuilt on the DS.
 *
 * The single owner of the `UILiveSessionReportDetails` model: it destructures the report once and lays
 * out a top `<ReportHero>` section followed by the section cards below it (siblings, not nested). Pure
 * composition — data in as the typed report, events out as callbacks; no Redux, no navigation, fully
 * renderable in Storybook.
 *
 * Full layout: the hero, "Bilan de la session", satisfaction, the notions covered, the engagement score,
 * the chapter sections, the three session times, the session activities, and the bottom "conseiller"
 * (Sam) CTA — each a sibling section under the stack.
 *
 * Notions and session times only carry data on a V1 report, and the sections they sit between are the
 * legacy order (`LiveReportComponent:1035` and `:1153`): a V1 report shows notions + the three times
 * where a V2 one shows its scores and its chapters.
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { ReportHero } from "../../molecules/ReportHero"
import { BilanCard } from "../../molecules/BilanCard"
import { SatisfactionCard } from "../../molecules/SatisfactionCard"
import { LiveEngagementScoreCard } from "../../organisms/LiveEngagementScoreCard"
import { ReportConceptsCard } from "../../organisms/ReportConceptsCard"
import { ReportChapterSectionCard } from "../../organisms/ReportChapterSectionCard"
import { ReportSessionTimesCard } from "../../organisms/ReportSessionTimesCard"
import { SessionActivitiesCard } from "../../organisms/SessionActivitiesCard"
import { ReportSamCta } from "../../molecules/ReportSamCta"
import { type UILiveSessionReportDetails } from "../../../ports/primary/ui/types"
import { Card } from "../../molecules/Card"

export type LiveReportDetailsTemplateProps = {
    report: UILiveSessionReportDetails
    /** "Je prends rdv avec mon conseiller" (Sam CTA). */
    onSamPress?: () => void
}

export function LiveReportDetailsTemplate(props: LiveReportDetailsTemplateProps) {
    const { report, onSamPress } = props

    return (
        <Card p="4">
            <Stack gap="5">
                <ReportHero
                    date={report.date}
                    studentDisplayName={report.studentDisplayName}
                    zoeVariant={report.zoeVariant}
                    heroMessage={report.heroMessage}
                    showSamCalendlyCta={report.showSamCalendlyCta}
                    onSamPress={onSamPress}
                />
                {report.commentedReport != null && <BilanCard comments={report.commentedReport} />}
                {report.showSamCalendlyCta && report.satisfaction != null && report.satisfaction >= 5 && (
                    <SatisfactionCard studentDisplayName={report.studentDisplayName} rating={report.satisfaction} />
                )}
                {report.conceptGroups != null && <ReportConceptsCard groups={report.conceptGroups} />}
                {report.scores != null && <LiveEngagementScoreCard scores={report.scores} />}
                {report.chapterSections.map((section, index) => (
                    <ReportChapterSectionCard
                        key={index}
                        title={section.title}
                        assessment={section.assessment}
                        chapters={section.chapters}
                    />
                ))}
                {report.sessionTimes != null && <ReportSessionTimesCard times={report.sessionTimes} />}
                {(report.validatedActivities.length > 0 || report.todoActivities.length > 0) && (
                    <SessionActivitiesCard validated={report.validatedActivities} todo={report.todoActivities} />
                )}
                {report.samMessage != null && report.samMessage !== "" && (
                    <ReportSamCta message={report.samMessage} onSamPress={onSamPress} />
                )}
            </Stack>
        </Card>
    )
}
